import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, of } from 'rxjs';
import { InfoService } from '@app/core/info/info.service';
import { IPackData, IPackItem, OrderStep } from '@app/certificates/packs/packs';
import { IPaymentDataPostResponse, PaymentStatusCode, IPaymentDataStatusResponse } from '@app/shared/interfaces/response-interfaces';
import { Store } from '@ngrx/store';
import { ActionPacksPaymentFailed } from '@app/certificates/packs/packs.reducer';
import { TranslateService } from '@ngx-translate/core';
import { PackService } from '@app/certificates/packs/pack.service';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { delay, map, retry, takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from '@app/config/app.config';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { PaymentData, PaymentDataStatus } from '@app/certificates/history/history';
import { HttpErrorHandler, HandleError } from '@app/shared/services/http-error-handler.service';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'cloud-pack-payment',
  templateUrl: './pack-payment.component.html',
  styleUrls: ['./pack-payment.component.scss']
})
export class PackPaymentComponent implements OnInit, OnDestroy {

  @Input() packData: IPackData;
  @Input() selectedPack: IPackItem;
  private handleError: HandleError;
  unsubscribe$: Subject<void> = new Subject<void>();
  icons = iconCodes;
  paymentInitiated: boolean;
  showMPIResponse: boolean;
  showPaymentInWaiting: boolean;
  readonly dateFormat = environment.dateFormat;

  paymentData: PaymentData = null;
  paymentDataStatus: PaymentDataStatus = null;

  constructor(
    public info: InfoService,
    private store: Store<any>,
    private translateService: TranslateService,
    private packService: PackService,
    private router: Router,
    private httpErrorHandler: HttpErrorHandler,
    private http: HttpClient,
  ) {
    this.handleError = httpErrorHandler.createHandleError('PackService');
  }

  ngOnInit(): void {
    this.info.dismissAll();
    this.paymentData = null;
    this.paymentInitiated = false;
    this.showMPIResponse = false;
    this.showPaymentInWaiting = false;
  }

  onCancelPayment() {
    this.paymentData = null;
    this.store.dispatch(new ActionPacksPaymentFailed(this.packData.order));
    this.stopPullingInMillis(0);
    setTimeout(() => {window.location.reload();}, 1000);
  }

  onInitiatePayment() {
    const payOrderNumber = this.orderNumber;
    this.packService.getPaymentData(payOrderNumber, this.packData.order.price.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: IPaymentDataPostResponse) => {
        if (response.status === 200) {
          this.paymentData = response.data;

          const dataForMPI = {
            AMOUNT: this.paymentData.amount,
            CURRENCY: this.paymentData.currency,
            DESC: this.paymentData.description + this.translateService.instant("packsTexts.packByRequest") + payOrderNumber,
            TERMINAL: this.paymentData.terminal,
            MERCH_NAME: this.paymentData.merchName,
            MERCHANT: this.paymentData.merchant,
            TRTYPE: this.paymentData.trType,
            ORDER: this.paymentData.order,
            TIMESTAMP: this.paymentData.timestamp,
            NONCE: this.paymentData.nonce,
            LANG: this.paymentData.lang,
            BACKREF: this.paymentData.backRef,
            ADDENDUM: this.paymentData.addendum,
            'AD.CUST_BOR_ORDER_ID': this.paymentData.adCustBorOrderId,
            P_SIGN:this.paymentData.pSign
          }

        this.prepareDataToSendAndCallMPI(dataForMPI);

        this.paymentInitiated = true;
      }

      });
  setTimeout(() => {
    this.pullForConfirmation();
  }, 5000);
  }

prepareDataToSendAndCallMPI(data: any): void {

    let mapForm = document.createElement("form");
    mapForm.target = "_blank";
    mapForm.method = "POST";
    mapForm.action = environment.mpiPaymentLink;
    Object.keys(data).forEach(function(param){
      let mapInput = document.createElement("input");
      mapInput.type = "hidden";
      mapInput.name = param;
      mapInput.setAttribute("value", data[param]);
      mapForm.appendChild(mapInput);
    });
    document.body.appendChild(mapForm);

    mapForm.submit();
}

get isInfinite() {
  return this.packData.order.planId === 3;
}

get lang(): string {
  return this.translateService.currentLang;
}

get packCount(): number {
  const cntTranslationCode = this.packService.getPackCountTranslationCode(this.packInfo.count);
  return this.translateService.instant(cntTranslationCode, { value: this.packInfo.count });
}

get shouldRenderComponent(): boolean {
  return this.packData && this.packData.step === OrderStep.PAYMENT;
}

get packInfo(): IPackItem {
  return this.packData.items.filter((item) => item.id === this.packData.order.planId)[0];
}

get showPaymentButton(): boolean {
  return this.packData.paymentStatus === PaymentStatusCode.NEW;
}

get awaitingPayment() {
  return this.paymentInitiated
    || this.packData.paymentStatus === PaymentStatusCode.WAITING_PAYMENT_IN_PROGRESS;
}

isCancelButtonDisabled(): boolean {
  return this.paymentData === null || this.paymentData === undefined;
}

get cardPaymentDescription(): string {

  if (this.awaitingPayment) {
    return 'packsTexts.cardPaymentInitiated';
  } else {
    switch (this.packData.paymentStatus) {
      case PaymentStatusCode.FINISHED_SUCCESS:
        return 'packsTexts.cardPaymentSuccess';
      case PaymentStatusCode.FINISHED_REJECTED:
        return 'packsTexts.cardPaymentFailed';
      case PaymentStatusCode.WAITING_PAYMENT_IN_PROGRESS:
        return 'packsTexts.cardPaymentInitiated';
      default:
        return 'packsTexts.cardPaymentDescription';
    }
  }
}

ngOnDestroy(): void {
  this.unsubscribe$.next();
  this.unsubscribe$.complete();
}

public pullForConfirmation() {
  this.stopPullingInMillis(900000);//15min
  this.packService.getMPIPaymentStatus(this.paymentData.order)
    .pipe(
      takeUntil(this.unsubscribe$),
      delay(AppConfig.paymentStatusTimeout),
      map((response: IPaymentDataStatusResponse) => {
        if (!this.paymentInitiated) {
          return null;
        }
 
        this.paymentDataStatus = response.data;
        if (this.paymentDataStatus.statusCode === PaymentStatusCode.WAITING_PAYMENT_IN_PROGRESS) {
          this.showMPIResponse = true;
          this.showPaymentInWaiting = true;
          throw this.paymentDataStatus.statusCode;
        }
        return response;
      }),
      retry({delay: errors => of(errors).pipe(tap(val => console.log('Check for pending order: received ' + val + '. Retrying...')))})
    )
    .subscribe((response: IPaymentDataStatusResponse) => {

      if (!this.paymentInitiated) {
        return;
      }
    
      this.paymentDataStatus = response.data;

      if (this.paymentDataStatus.statusCode === PaymentStatusCode.FINISHED_REJECTED) {
        this.showPaymentInWaiting = false;
        this.packService.updatePaymentStatus(this.packData.order, response);
        this.paymentData = null;
      } else if (this.paymentDataStatus.statusCode === PaymentStatusCode.FINISHED_SUCCESS) {
        this.showPaymentInWaiting = false;
        this.packService.updatePaymentStatus(this.packData.order, response);
        this.paymentData = null;
      } else if (this.paymentDataStatus.statusCode === PaymentStatusCode.ERROR) {
        this.showPaymentInWaiting = false;
        this.packService.updatePaymentStatus(this.packData.order, response);
        this.paymentData = null;
      }
    });
}

stopPullingInMillis(time : number) : void {

  setTimeout( ()=>{
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    }, time);
}

backAfterSuccessfullyPayment() {
  this.router.navigateByUrl('/manage/history');
}

get orderNumber(): any {
  return this.packData.order.axOrderNumber !== null ? this.packData.order.axOrderNumber : this.packData.order.orderCode;
}

//Check for blank, null or undefined
isBlank(text : string) : boolean {
  return (!text || /^\s*$/.test(text));
}
}
